import { Team } from "@entities";
import { Box, Button, Stack, Typography } from "@mui/material";
import useCreateHyperTable from "@shared/frontend/hyper-table";
import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { useOrganization } from "../../contexts/OrganizationProvider";

export interface TeamsSelectTableProps {
  id?: string;
  onSave?: (teams: (string | Team)[]) => void;
  onChange?: (selectedTeams: (string | Team)[]) => void;
  value?: (string | Team)[];
  isOne?: boolean;
  isBasicTeam?: boolean;
  returnFullTeam?: boolean;
  preventCellClick?: boolean; // New prop added here
}

const TeamsSelectTable: React.FC<TeamsSelectTableProps> = (props) => {
  const { pathname } = useLocation();
  const {
    id = pathname,
    value = [],
    onChange,
    onSave,
    isBasicTeam = true,
    isOne,
    returnFullTeam = false,
  } = props;
  const { currOrganization } = useOrganization();
  const HyperTable = useCreateHyperTable(Team);
  const [selectedTeams, setSelectedTeams] = useState<(string | Team)[]>(value);

  useEffect(() => {
    onChange?.(selectedTeams);
  }, [JSON.stringify(selectedTeams)]);

  return (
    <Stack width="100%" height="100%" minHeight={0} overflow="hidden">
      <Box flex={1} minHeight={0} overflow="hidden">
        <HyperTable
      components={{
        scrollToTop: () => null,
        rowCounter: null,
        paginate: onSave ? null : undefined,
      }}
      onDisplayedDataChange={() => {
        const ctx = HyperTable.context.current;
        if (!ctx) return;
        const { table } = ctx;
        table
          .getFilteredRowModel()
          .flatRows.filter((row) =>
            selectedTeams?.some((t) =>
              typeof t === "string"
                ? t === row.original.id
                : t.id === row.original.id,
            ),
          )
          .forEach((row) =>
            row.toggleSelected(true, { selectChildren: false }),
          );
      }}
      findConfig={{
        limit: 50,
        where: Team.hyperFilter({
          organization: {
            $id: currOrganization?.id,
          },
          parentTeam: {
            $is: null,
          },
        }),
        enabled: !!currOrganization,
        include: { associations: false },
        initialOrderBy: { isMainOrganizationTeam: "desc", name: "asc", },
      }}
      tableSettings={{
        enableMultiRowSelection: !isOne,
        getSubRows: (row) =>
          row.subTeams
            .filter((item) => !!item.subTeam)
            .map((item) => item.subTeam!),
      }}
      tableId={`teams-select-table-${id}`}
    >
      <HyperTable.Toolbar disableViewOptions>
        <Typography padding="0 2%" mb={1} color="grey" fontSize="small">
          <b>יש לשים לב - </b> סימון פרויקט יסמן רק את הפרויקט עצמו - ללא תתי
          הפרויקטים.
        </Typography>
      </HyperTable.Toolbar>
      <HyperTable.SelectColumn
        selectAllBehavior="single-page"
        onItemChange={(row, selected) => {
          setSelectedTeams((prev) => {
            const copy = [...prev];
            const index = copy.findIndex(
              (v) => row.id === (returnFullTeam ? (v as Team).id : v),
            );

            if ((selected && index !== -1) || (!selected && index === -1))
              return prev;

            if (selected) {
              copy.push(returnFullTeam ? row : row.id);
            } else {
              copy.splice(index, 1);
            }

            return copy;
          });
        }}
        onSelectAllChange={(v) =>
          HyperTable.context.current?.table.toggleAllRowsExpanded(v)
        }
        selectChildren={false}
        minSize={50}
        size={50}
      />
      <HyperTable.ExpandColumn minSize={56} size={56} />
      <HyperTable.Column
        field="name"
        maxSize={undefined}
        enablePinning={false}
        defaultPinned
        dontShowHeaderDivider
        minSize={160}
      />
      </HyperTable>
      </Box>
      {!!onSave && (
        <Stack direction="row" justifyContent="flex-end" pt={1.5} flexShrink={0}>
          <Button onClick={() => onSave(selectedTeams)} variant="contained">
            שמירה
          </Button>
        </Stack>
      )}
    </Stack>
  );
};

export default TeamsSelectTable;
